import {TrackingKeyVariants} from '@/components/tracked-atoms/types';
import {ArrayGroup, Group, GroupModalButton, LocalizedString, ValueDefinition} from '@/model/types';
import {buildDependencyGraph, createDependencyRegistrer} from '../dependencies';
import {
  DependencyMap,
  AccessMap,
  ModelGroup,
  ModelArrayGroup,
  ModelBaseGroup,
  ModelGroupModalButton,
  GroupAccessRecord,
  SectionAccessRecord,
  DependencyRegistrer,
} from '../types';
import {createArrayRowsFactory} from './array';
import {createConditionFactory} from './condition';
import {createEventHandler} from './event';
import {computeTitle} from './factories';
import {mapToModelField} from './field';
import {createFactoryFromSwitch} from './switch';

const mapToModelGroupModalButton = (
  button: GroupModalButton,
  dependencyRegistrer: DependencyRegistrer,
): ModelGroupModalButton => {
  const label =
    typeof button.label === 'object' && '$switch' in button.label
      ? createFactoryFromSwitch(button.label, {fr: '', en: ''} as LocalizedString, {dependencyRegistrer})
      : () => button.label as LocalizedString;

  return {
    label,
    theme: button.theme,
    trackingKey: button.trackingKey as TrackingKeyVariants,
    onClick: button.onClick.map(event => createEventHandler(event, dependencyRegistrer)),
  };
};

export const mapToModelBaseGroup = (
  sectionId: string,
  group: Group,
  values: ValueDefinition[],
  dependencyMap: DependencyMap,
  accessMap: AccessMap,
): ModelBaseGroup => {
  const {title, displayMode, hidden, displayIf, fields, onInit, onMount, onSave, onCancel, onDelete, buttons, ...rest} =
    group;

  const dependencyRegistrer = createDependencyRegistrer(dependencyMap, group.id);

  const mappedGroup = {
    ...rest,
    title: computeTitle(title, dependencyRegistrer),
    displayMode: displayMode ?? 'inline',
    hidden: hidden ?? false,
    displayIf: displayIf
      ? displayIf.map(condition => createConditionFactory(condition, dependencyRegistrer, 'visibility'))
      : [() => () => true],
    onInit: (onInit ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
    onMount: (onMount ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
    onSave: (onSave ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
    onCancel: (onCancel ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
    onDelete: (onDelete ?? []).map(event => createEventHandler(event, dependencyRegistrer)),
    buttons: (buttons ?? []).map(button => mapToModelGroupModalButton(button, dependencyRegistrer)),
    fields: fields.map((field, fieldIndex) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const sectionIndex = (accessMap.get(sectionId)! as SectionAccessRecord).sectionIndex;
      const groupId = group.id.includes('#') ? group.id.slice(0, group.id.indexOf('#')) : group.id;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const groupIndex = (accessMap.get(groupId)! as GroupAccessRecord).groupIndex;
      accessMap.set(field.id, {sectionIndex, groupIndex, fieldIndex});

      return mapToModelField(field, values, dependencyMap);
    }),
  };

  buildDependencyGraph(dependencyMap, group.id, displayIf?.length ? group.fields.map(field => field.id) : []);

  return mappedGroup;
};

export const mapToModelArrayGroup = (
  sectionId: string,
  group: ArrayGroup,
  values: ValueDefinition[],
  dependencyMap: DependencyMap,
  accessMap: AccessMap,
): ModelArrayGroup => {
  const {id, type, options, fields, ...baseGroupFields} = group;

  const dependencyRegistrer = createDependencyRegistrer(dependencyMap, id);

  return {
    id: id,
    type: type,
    ...createArrayRowsFactory(
      sectionId,
      id,
      options,
      fields,
      baseGroupFields,
      values,
      dependencyMap,
      accessMap,
      dependencyRegistrer,
    ),
  };
};

export const mapToModelGroup = (
  sectionId: string,
  group: Group,
  values: ValueDefinition[],
  dependencyMap: DependencyMap,
  accessMap: AccessMap,
): ModelGroup => {
  if ('type' in group) {
    switch (group.type) {
      case 'array':
        return mapToModelArrayGroup(sectionId, group, values, dependencyMap, accessMap);
      default:
        throw new Error(`Unsupported group type: ${group.type}`);
    }
  }

  return mapToModelBaseGroup(sectionId, group, values, dependencyMap, accessMap);
};
